/**
 * Locations Service
 * 
 * This service handles API calls related to location management (depots and demand points)
 */

import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:5000';


export default {
  /**
   * Get all locations
   * @param {string|null} type Optional type filter (e.g., 'depot' or 'demand')
   * @returns {Promise} Promise with the location data
   */
  async getLocations(type = null) {
    const url = type ? `${API_URL}/api/locations?type=${type}` : `${API_URL}/api/locations`;
    return axios.get(url);
  },
  
  /**
   * Get all depots (specialized endpoint for depots only)
   * This endpoint is optimized for better performance
   * @returns {Promise} Promise with the depots data
   */
  getDepots() {
    return axios.get(`${API_URL}/api/depots`);
  },
  
  /**
   * Get a single location by ID
   * @param {string} id Location ID
   * @returns {Promise} Promise with the location data
   */
  getLocation(id) {
    return axios.get(`${API_URL}/api/locations/${id}`);
  },
  
  /**
   * Create a new location
   * @param {Object} locationData Location data
   * @returns {Promise} Promise with the created location
   */
  createLocation(locationData) {
    return axios.post(`${API_URL}/api/locations`, locationData);
  },
  
  /**
   * Update an existing location
   * @param {string} id Location ID
   * @param {Object} locationData Updated location data
   * @returns {Promise} Promise with the updated location
   */
  updateLocation(id, locationData) {
    return axios.put(`${API_URL}/api/locations/${id}`, locationData);
  },
  
  /**
   * Delete a location
   * @param {string} id Location ID
   * @returns {Promise} Promise that resolves once deletion is complete
   */
  deleteLocation(id) {
    return axios.delete(`${API_URL}/api/locations/${id}`);
  },
  
  /**
   * Get all demand point locations
   * @returns {Promise} Promise with demand point locations
   */
  getDemandPoints() {
    return axios.get(`${API_URL}/api/locations?type=demand`);
  },

  // Collection Management Methods
  
  /**
   * Get all location collections
   * @returns {Promise} Promise with collections data
   */
  getCollections() {
    return axios.get(`${API_URL}/api/locations/collections`);
  },
  
  /**
   * Get locations in a specific collection
   * @param {string} collectionId Collection ID
   * @returns {Promise} Promise with collection locations
   */
  getCollectionLocations(collectionId) {
    return axios.get(`${API_URL}/api/locations/collections/${collectionId}`);
  },
  
  /**
   * Create a new location collection with bulk locations upload
   * @param {Object} collectionData Collection data with locations
   * @returns {Promise} Promise with the created collection
   */
  createLocationCollection(collectionData) {
    return axios.post(`${API_URL}/api/locations/collections`, collectionData);
  },
  
  /**
   * Delete a location collection
   * @param {string} collectionId Collection ID
   * @param {boolean} deleteLocations Whether to delete associated locations
   * @returns {Promise} Promise that resolves once deletion is complete
   */
  deleteCollection(collectionId, deleteLocations = true) {
    return axios.delete(`${API_URL}/api/locations/collections/${collectionId}?deleteLocations=${deleteLocations}`);
  },

  /**
   * Get depots from a specific collection
   * @param {string} collectionId Collection ID
   * @returns {Promise} Promise with depot locations from the collection
   */
  getCollectionDepots(collectionId) {
    return axios.get(`${API_URL}/api/locations/collections/${collectionId}`).then(response => {
      // Filter for depot type locations
      const depots = response.data.filter(location => location.type === 'depot');
      return { data: depots };
    });
  },

  /**
   * Get demand points from a specific collection
   * @param {string} collectionId Collection ID
   * @returns {Promise} Promise with demand point locations from the collection
   */
  getCollectionDemandPoints(collectionId) {
    return axios.get(`${API_URL}/api/locations/collections/${collectionId}`).then(response => {
      // Filter for demand type locations
      const demandPoints = response.data.filter(location => location.type === 'demand');
      return { data: demandPoints };
    });
  },

  /**
   * Get locations within specified geographical bounds
   * @param {Object} bounds Bounding box with north, south, east, west properties
   * @param {string|null} type Optional type filter
   * @returns {Promise} Promise with locations within bounds
   */
  getLocationsByBounds(bounds, type = null) {
    const params = new URLSearchParams({
      north: bounds.north,
      south: bounds.south,
      east: bounds.east,
      west: bounds.west
    });
    
    if (type) {
      params.append('type', type);
    }
    
    return axios.get(`${API_URL}/api/locations/bounds?${params.toString()}`);
  },

  /**
   * Get vendor IDs for a list of location names
   * @param {Array<string>} locationNames Array of location names
   * @returns {Promise} Promise with a map of location names to vendor IDs
   */
  getVendorIds(locationNames) {
    return axios.post(`${API_URL}/api/locations/vendor-ids`, { location_names: locationNames });
  }
};
